import { HelpPost } from '@professocialApi/professocial-core-frontend-api/models'
import { useTranslation } from 'react-i18next'
import { Box, Paper, Theme, Typography, Snackbar, Alert } from '@mui/material'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import VideoComponent from '@professocial/professocialFormComponents/FormComponents/VideoComponent'
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import MenuComponent from '@professocial/professocialFormComponents/FormComponents/MenuComponent'
import { useHistory } from 'react-router-dom'
import DOMPurify from 'dompurify'

export interface Props {
  dataEntry: HelpPost
  index?: number
  deleteHelpPostSelected: (id: string) => void
  basePath?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    padding: '0px',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    minHeight: '100%',
    objectFit: 'cover',
    borderTopLeftRadius: '2%',
    borderTopRightRadius: '2%',
  },
  content: {
    padding: theme.spacing(2),
  },
  buttons: {
    padding: theme.spacing(2),
  },
  actionItem: {
    '&.MuiButton-root': {
      textTransform: 'uppercase',
    },
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  imageWraper: {
    width: '100%',
    minHeight: theme.spacing(19),
    maxHeight: theme.spacing(19),
    overflow: 'hidden',
  },
  standardTopic: {
    paddingRight: theme.spacing(1),
  },
  descriptionBox: {
    margin: theme.spacing(1, 0),
    width: '100%',
    overflow: 'hidden',
  },
  footer: {
    height: '40px',
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 4, /* number of lines to show */
    lineClamp: 4,
    WebkitBoxOrient: 'vertical'
  }
}))

const EditorHelpPostCard: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const history = useHistory()
  const basePath = props.basePath || ''

  const [showDeleteHelpPostWarning, setShowDeleteHelpPostWarning] = useState<boolean>(false)
  const [dataEntry, setDataEntry] = useState<HelpPost>(props.dataEntry)
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)

  const [description, setDescription] = useState(dataEntry.text)
  const descriptionRef = React.createRef<HTMLDivElement>()

  useEffect(() => {
    setDataEntry(props.dataEntry)
    setDescription(props.dataEntry.text)
  }, [props])

  const handleCopyDeeplink = () => {
    if (dataEntry.entityId) {
      const deeplink = `${basePath}/user/help?id=${dataEntry.entityId}`
      navigator.clipboard.writeText(deeplink)
        .then(() => {
          setSnackbarOpen(true)
        })
        .catch((error) => {
          console.error('Failed to copy deeplink: ', error)
        })
    }
  }

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbarOpen(false)
  }

  return (
    <>
      <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden' }}>
        <Box className={classes.card}>
          <Box className={classes.imageWraper} display='flex' justifyContent='center'>
            <Box sx={{ display: 'contents' }}>
              {dataEntry.picture && !dataEntry.video && !dataEntry.videoUrl && <ImageComponent src={dataEntry.picture} className={classes.image} fallback='/src/shared/assets/images/icon-img-placeholder.svg' />}
              {dataEntry.video && <VideoComponent src={dataEntry.video.videoUrl} controls className={classes.image} fallbackThumb='/src/shared/assets/images/icon-img-placeholder.svg' />}
              {dataEntry.videoUrl && <VideoComponent src={dataEntry.videoUrl} type='YT_VIDEO' height={152} fallbackThumb='/src/shared/assets/images/icon-img-placeholder.svg' />}
            </Box>
          </Box>
          <Box className={classes.content}>
            <Box display='flex' flexWrap='wrap' alignItems='center'>
              <Typography><b>{dataEntry.headline}</b></Typography>
            </Box>
            <Box>
              <div className={classes.descriptionBox} ref={descriptionRef} id={String(description)}>
                {description && <Box>
                  <Typography
                    variant="body1"
                    className={classes.ellipsis}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description, { ADD_ATTR: ['target'] }) }}
                  />
                </Box>}
              </div>
            </Box>
          </Box>
          <Box sx={{ py: 1, display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
            <MenuComponent
              className='primary'
              menuItem={[
                {
                  label: t('topicsCommon:edit'),
                  value: 'edit',
                  handleClick: () => history.push('/editor/help/edit/' + dataEntry.entityId),
                },
                {
                  label: t('topicsCommon:delete'),
                  value: 'delete',
                  handleClick: () => setShowDeleteHelpPostWarning(true),
                },
                {
                  label: 'Deeplink kopieren',
                  value: 'copyDeeplink',
                  handleClick: handleCopyDeeplink,
                },
              ]}
            />
          </Box>
        </Box>
        <DialogComponent
          type='error'
          title={t('networkContentMessages:deleteWarningTitle')}
          message={t('topicsMessages:deleteTopicWarning')}
          open={showDeleteHelpPostWarning}
          confirmLabel={t('networkContentCommon:delete')}
          cancelLabel={t('networkContentCommon:cancel')}
          autoClose={false}
          requestActions
          onClose={() => setShowDeleteHelpPostWarning(false)}
          onCancel={() => setShowDeleteHelpPostWarning(false)}
          onConfirm={() => {
            setShowDeleteHelpPostWarning(false)
            props.deleteHelpPostSelected && dataEntry.entityId && props.deleteHelpPostSelected(dataEntry.entityId)
          }}
        />
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Deeplink wurde in die Zwischenablage kopiert
        </Alert>
      </Snackbar>
    </>
  )
}

export default EditorHelpPostCard
